// src/pages/LawyerDetailPage.jsx
import { useState } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { getLawyerBySlug } from '../data/lawyers';
import { coverageItems } from '../data/coverage';
import { Inquiry } from '../features/Inquiry/Inquiry';
import styles from './LawyerDetailPage.module.css';
import { Seo } from '../components/Seo';
import { personJsonLd, breadcrumbJsonLd } from '../lib/structuredData';

const COLLAPSE_LIMIT = 7;

// "2017~ 법무법인 오킴스", "2008~2009 대한공중보건의사협의회" 같은
// 연도 접두 문자열에서 연도를 분리
const YEAR_RE = /^(\d{4}(?:~\d{4}|~)?)\s+(.*)$/;
function splitYear(line) {
  const m = YEAR_RE.exec(line);
  if (!m) return { year: null, text: line };
  return { year: m[1], text: m[2] };
}

// 리스트가 limit을 넘으면 처음 N개만 보이고 "더보기" 버튼으로 펼친다
function ExpandableList({ items, limit = COLLAPSE_LIMIT, render }) {
  const [expanded, setExpanded] = useState(false);
  const isOver = items.length > limit;
  const visible = isOver && !expanded ? items.slice(0, limit) : items;
  return (
    <>
      {render(visible)}
      {isOver && (
        <button
          type="button"
          className={styles.expandToggle}
          onClick={() => setExpanded((e) => !e)}
          aria-expanded={expanded}
        >
          {expanded ? '접기' : `더보기 +${items.length - limit}`}
          <svg
            className={`${styles.expandCaret} ${expanded ? styles.expandCaretOpen : ''}`}
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M3 4.5l3 3 3-3"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
    </>
  );
}

function Timeline({ items }) {
  return (
    <ul className={styles.timeline}>
      {items.map((raw, i) => {
        const { year, text } = splitYear(raw);
        return (
          <li
            key={i}
            className={`${styles.timelineItem} ${!year ? styles.timelineItemNoYear : ''}`}
          >
            <span className={styles.timelineDot} />
            {year && <span className={styles.timelineYear}>{year}</span>}
            <span className={styles.timelineText}>{text}</span>
          </li>
        );
      })}
    </ul>
  );
}

function Block({ heading, children }) {
  return (
    <section className={styles.block}>
      <h2 className={styles.blockHeading}>{heading}</h2>
      {children}
    </section>
  );
}

export function LawyerDetailPage() {
  const { slug } = useParams();
  const lawyer = getLawyerBySlug(slug);

  if (!lawyer) {
    return <Navigate to="/" replace />;
  }

  const hasNotableCases = lawyer.notableCases && Object.keys(lawyer.notableCases).length > 0;
  const seoDescription = (lawyer.summary || lawyer.landingNarrative || '').slice(0, 150);

  return (
    <main className={styles.main}>
      <Seo
        title={`${lawyer.name} 변호사 — medilawyer`}
        description={seoDescription}
        path={`/lawyer/${lawyer.slug}`}
        jsonLd={[
          personJsonLd({
            name: `${lawyer.name} 변호사`,
            jobTitle: lawyer.role,
            description: seoDescription,
            path: `/lawyer/${lawyer.slug}`,
          }),
          breadcrumbJsonLd([
            { name: '홈', path: '/' },
            { name: '구성원', path: '/#expert' },
            { name: `${lawyer.name} 변호사`, path: `/lawyer/${lawyer.slug}` },
          ]),
        ]}
      />
      <div className={styles.topBar}>
        <div className="wrap">
          <Link to="/#expert" className={styles.back} aria-label="돌아가기">
            <svg
              className={styles.backIcon}
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M13 8H3M7 4L3 8l4 4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>돌아가기</span>
          </Link>
        </div>
      </div>

      <div className={styles.layoutWrap}>
        <div className="wrap">
          <div className={styles.layout}>
            {/* ── Left: sticky profile ── */}
            <aside className={styles.left}>
              <div className={styles.leftInner}>
                <div className={styles.photoWrap}>
                  {lawyer.photo ? (
                    <img src={lawyer.photo} alt={lawyer.photoAlt} className={styles.photo} />
                  ) : (
                    <div className={styles.photoFallback} aria-label={lawyer.photoAlt}>
                      <svg viewBox="0 0 120 160" fill="none" aria-hidden="true">
                        <rect width="120" height="160" fill="currentColor" opacity="0.08" />
                        <circle cx="60" cy="58" r="22" fill="currentColor" opacity="0.25" />
                        <path
                          d="M20 160 C 20 115, 40 95, 60 95 C 80 95, 100 115, 100 160 Z"
                          fill="currentColor"
                          opacity="0.25"
                        />
                      </svg>
                    </div>
                  )}
                </div>

                <h1 className={styles.name}>{lawyer.name} 변호사</h1>
                <div className={styles.role}>{lawyer.role}</div>
                <p className={styles.summary}>{lawyer.summary}</p>

                <div className={styles.contactRow}>
                  {lawyer.contact?.tel && (
                    <a href={`tel:${lawyer.contact.tel}`} className={styles.contactBtn}>
                      TEL {lawyer.contact.tel}
                    </a>
                  )}
                  {lawyer.contact?.email && (
                    <a href={`mailto:${lawyer.contact.email}`} className={styles.contactBtn}>
                      {lawyer.contact.email}
                    </a>
                  )}
                </div>

                <a href="#page-inquiry" className={styles.cta}>
                  {lawyer.name} 변호사에게 상담 요청
                </a>
              </div>
            </aside>

            {/* ── Right: 연혁·이력 ── */}
            <div className={styles.right}>
              {/* Solution promise */}
              <div className={styles.promise}>
                <span className={styles.promiseEyebrow}>How we help</span>
                <h2 className={styles.promiseTitle}>
                  원장님이 진료에 집중하실 수 있도록,
                  <br />
                  아래 분쟁 상황을 <span className={styles.promiseAccent}>직접 대응</span>합니다.
                </h2>
                <p className={styles.promiseSub}>
                  의사 출신 관점에서 의학적 쟁점을 판단하고, 변호사로서 법적 절차를 동시에
                  수행합니다. 클레임 초기 대응부터 형사 고소·민사 소송까지, 동일한 팀이 일관되게
                  끌고 갑니다.
                </p>
              </div>

              <Block heading="이런 문제를 이렇게 해결합니다">
                <div className={styles.problemGrid}>
                  {coverageItems.slice(0, 6).map((p) => (
                    <Link key={p.slug} to={`/coverage/${p.slug}`} className={styles.problemCard}>
                      <div className={styles.problemHead}>
                        <span className={styles.problemN}>{p.n}</span>
                        <span className={styles.problemName}>{p.name}</span>
                      </div>
                      <div className={styles.problemPain}>{p.pain}</div>
                      <div className={styles.problemLink}>해결 방법 자세히 보기 →</div>
                    </Link>
                  ))}
                </div>
              </Block>

              {lawyer.credentials?.length > 0 && (
                <Block heading="핵심 자격">
                  <ul className={styles.chipList}>
                    {lawyer.credentials.map((c, i) => (
                      <li key={i} className={styles.chip}>
                        {c.label}
                      </li>
                    ))}
                  </ul>
                </Block>
              )}

              {lawyer.career?.length > 0 && (
                <Block heading="경력">
                  <ExpandableList
                    items={lawyer.career}
                    render={(visible) => <Timeline items={visible} />}
                  />
                </Block>
              )}

              {lawyer.practiceAreas?.length > 0 && (
                <Block heading="업무분야">
                  <ul className={styles.chipList}>
                    {lawyer.practiceAreas.map((a, i) => (
                      <li key={i} className={styles.chip}>
                        {a}
                      </li>
                    ))}
                  </ul>
                </Block>
              )}

              {hasNotableCases && (
                <Block heading="주요 실적">
                  {Object.entries(lawyer.notableCases).map(([category, cases]) => (
                    <div key={category} className={styles.casesGroup}>
                      <h3 className={styles.casesSubHeading}>{category}</h3>
                      <ExpandableList
                        items={cases}
                        render={(visible) => <Timeline items={visible} />}
                      />
                    </div>
                  ))}
                </Block>
              )}

              {lawyer.activities?.length > 0 && (
                <Block heading="대외활동">
                  <ExpandableList
                    items={lawyer.activities}
                    render={(visible) => <Timeline items={visible} />}
                  />
                </Block>
              )}

              {lawyer.lectures?.length > 0 && (
                <Block heading="강연 및 저술">
                  <ExpandableList
                    items={lawyer.lectures}
                    render={(visible) => <Timeline items={visible} />}
                  />
                </Block>
              )}

              {(lawyer.education?.length > 0 || lawyer.certifications?.length > 0) && (
                <Block heading="학력 및 자격">
                  <div className={styles.twoCol}>
                    {lawyer.education?.length > 0 && (
                      <div>
                        <h3 className={styles.subHeading}>학력</h3>
                        <ExpandableList
                          items={lawyer.education}
                          render={(visible) => <Timeline items={visible} />}
                        />
                      </div>
                    )}
                    {lawyer.certifications?.length > 0 && (
                      <div>
                        <h3 className={styles.subHeading}>자격</h3>
                        <ExpandableList
                          items={lawyer.certifications}
                          render={(visible) => (
                            <ul className={styles.plainList}>
                              {visible.map((c, i) => (
                                <li key={i} className={styles.plainItem}>
                                  {c}
                                </li>
                              ))}
                            </ul>
                          )}
                        />
                      </div>
                    )}
                  </div>
                </Block>
              )}

              {lawyer.languages?.length > 0 && (
                <Block heading="사용 언어">
                  <ul className={styles.chipList}>
                    {lawyer.languages.map((l, i) => (
                      <li key={i} className={styles.chip}>
                        {l}
                      </li>
                    ))}
                  </ul>
                </Block>
              )}
            </div>
          </div>
        </div>
      </div>

      <Inquiry inline serviceContext={`lawyer:${lawyer.slug}`} />
    </main>
  );
}
